import { Controller } from '@hotwired/stimulus'

import 'lightgallery.js'
import 'lightgallery.js/dist/css/lightgallery.min.css'
import Modal from 'bootstrap/js/src/modal'
import { initialiseFileValidator, validateFileSize } from '../../entrypoints/report/components/file_validation.js'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'

export default class extends Controller {
  static values = {
    recordId: String,
    recordType: String,
    fileCountMax: Number,
    fileCount: Number,
    checklistTaskId: String,
    imageMaxSize: Number,
    videoMaxSize: Number,
    audioMaxSize: Number,
    pdfMaxSize: Number,
    spreadsheetMaxSize: Number
  }

  static targets = [
    'recordMediaContainer',
    'createMedia',
    'lightgallery', 'imageLoading', 'imageLoadingSrc',
    'thumbnails', 'mediaLoading',
    'undoModal', 'removeModal',
    'validationModal', 'validationModalTitle', 'validationModalMessage',
    'hiddenContent',
    'dragAndDrop'
  ]

  undoModal = null
  removeModal = null
  validationModal = null
  mediaToRemove = null

  connect () {
    this.initLightGallery()
    this.initDragAndDrop()
    initialiseFileValidator(this.imageMaxSizeValue, this.videoMaxSizeValue, this.audioMaxSizeValue, this.pdfMaxSizeValue, this.spreadsheetMaxSizeValue)

    this.handleFileChange = (event) => {
      this.uploadFiles(event.currentTarget.files)
    }
    this.createMediaTarget.addEventListener('change', this.handleFileChange)

    this.undoModal = new Modal(this.undoModalTarget)
    this.removeModal = new Modal(this.removeModalTarget)
    this.validationModal = new Modal(this.validationModalTarget)
  }

  disconnect() {
    super.disconnect();

    this.createMediaTarget.removeEventListener('change', this.handleFileChange)
  }

  initLightGallery () {
    this.lightgalleryTargets.forEach((lg) => {
      lg.addEventListener('onBeforeOpen', function (e) {
        $('#sidenav-main').addClass('d-none')
      }, false)

      lg.addEventListener('onBeforeClose', function (e) {
        $('#sidenav-main').removeClass('d-none')
      }, false)

      lightGallery(lg, {
        mode: 'lg-fade',
        thumbnail: true
      })
    })
  }

  initDragAndDrop () {
    const htmlElement = document.querySelector('html');

    htmlElement.addEventListener('dragover', (event) => {
      event.preventDefault()
      event.stopPropagation()
    })

    htmlElement.addEventListener('drop', (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.hideDragAndDropOverlay()
    })

    htmlElement.addEventListener('dragenter', (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.showDragAndDropOverlay()
    })

    htmlElement.addEventListener('mouseenter', (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.hideDragAndDropOverlay()
    })

    if (this.recordTypeValue === 'CHECKLIST') {
      this.element.addEventListener('drop', (event) => {
        this.handleDropEvent(event)
      })
    } else {
      if (this.hasDragAndDropTarget) {
        this.dragAndDropTarget.addEventListener('drop', (event) => {
          this.handleDropEvent(event)
        })
      }
    }
  }

  showDragAndDropOverlay () {
    if (this.recordTypeValue === 'CHECKLIST') {
      this.element.classList.add('checklist-task-drag-and-drop-overlay')
    } else {
      if (this.hasDragAndDropTarget) {
        this.dragAndDropTarget.classList.add('controls-card-overlay')
      }
    }
  }

  hideDragAndDropOverlay () {
    if (this.recordTypeValue === 'CHECKLIST') {
      this.element.classList.remove('checklist-task-drag-and-drop-overlay')
    } else {
      if (this.hasDragAndDropTarget) {
        this.dragAndDropTarget.classList.remove('controls-card-overlay')
      }
    }
  }

  handleDropEvent (event) {
    event.preventDefault()
    event.stopPropagation()
    this.hideDragAndDropOverlay()
    this.uploadFiles(event.dataTransfer.files)
  }

  showThumbnail (file) {
    if (this.hasHiddenContentTarget && !this.hiddenContentTarget.classList.contains('arrow-rotated')) {
      this.hiddenContentTarget.click()
    }

    if (file.type.startsWith('image')) {
      const fileReader = new FileReader()
      fileReader.readAsDataURL(file)
      fileReader.onload = (event) => {
        this.imageLoadingSrcTarget.src = event.target.result
        this.imageLoadingTarget.classList.remove('d-none')
      }
    } else {
      this.mediaLoadingTarget.classList.remove('d-none')
    }
  }

  isFileValid (file) {
    const validationResponseObject = validateFileSize(file)
    if (!validationResponseObject.isValid) {
      this.showValidationModal(validationResponseObject.title, validationResponseObject.errorMessage)
      return
    }

    return true
  }

  uploadFiles (files) {
    if (this.fileCountValue + files.length > this.fileCountMaxValue) {
      this.showValidationModal('Add Media Error', `There is a maximum of ${this.fileCountMaxValue} files.`)
      return
    }

    for (const file of [...files]) {
      if (this.isFileValid(file)) {
        this.showThumbnail(file)
        this.uploadFile(file)
      }
    }
  }

  uploadFile (file) {
    if (this.fileCountValue === 0) {
      this.recordMediaContainerTarget.classList.remove('d-none')
    }

    let url = `/internal/records/media?recordType=${this.recordTypeValue}`
    if (this.recordIdValue) {
      url += `&recordId=${this.recordIdValue}`
      if (this.checklistTaskIdValue) {
        url += `&checklistTaskId=${this.checklistTaskIdValue}`
      }
    }

    const data = new FormData()
    data.append('file', file)
    fetchWithHandlingLoginRedirect(url, { method: 'POST', body: data })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response)
        }

        this.fileCountValue++
        return response.json()
      })
      .then((json) => {
        this.hideThumbnail()
        if (json.mediaType === 'image') {
          this.addImageThumbnail(json.media)
        } else {
          this.addMediaThumbnail(json.media)
        }

        return json.mediaId
      })
      .then((mediaId) => {
        document.dispatchEvent(new CustomEvent('MediaUploaded', {
          detail: { mediaId: mediaId }
        }))
      })
      .catch((response) => {
        this.hideThumbnail()
        if (this.fileCountValue === 0) {
          this.recordMediaContainerTarget.classList.add('d-none')
        }

        switch (response.status) {
          case 403: this.showValidationModal('Add Media Error', 'Failed to add media. You do not have permission to add media to this record'); break
          case 413: this.showValidationModal('Add Media Error', 'Failed to add media. The file is too large. Please try again with a smaller file'); break
          case 504: this.showValidationModal('Add Media Error', 'Failed to add media. The request timed out. Please try again later'); break
          default: this.showValidationModal('Add Media Error', 'Failed to add media. Please try again later'); break
        }
      })
  }

  hideThumbnail () {
    this.imageLoadingTarget.classList.add('d-none')
    this.mediaLoadingTarget.classList.add('d-none')
  }

  addImageThumbnail (media) {
    this.lightgalleryTarget.insertAdjacentHTML('beforeend', media)
    this.initLightGallery()
  }

  addMediaThumbnail (media) {
    this.thumbnailsTarget.insertAdjacentHTML('beforeend', media)
  }

  remove (event) {
    if (event.detail.isUndo) {
      this.undoModal.show()
      this.mediaToRemove = event.currentTarget
    } else {
      this.removeModal.show()
      this.mediaToRemove = event.currentTarget
    }
  }

  confirmUndo () {
    this.undoModal.hide()
    this.removeMedia(this.mediaToRemove.dataset.mediaId, true)
  }

  confirmRemove () {
    this.removeModal.hide()
    this.removeMedia(this.mediaToRemove.dataset.mediaId, false)
  }

  removeMedia (mediaId, isUndo) {
    fetchWithHandlingLoginRedirect(`/internal/media/${mediaId}?recordType=${this.recordTypeValue}` + (isUndo ? `&${new URLSearchParams({ undo: 'true' })}` : ''), { method: 'DELETE' })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response)
        }

        return response.json
      })
      .then((json) => {
        this.mediaToRemove.remove()
        this.fileCountValue--
        if (this.fileCountValue === 0) {
          this.recordMediaContainerTarget.classList.add('d-none')
        }

        return json.mediaId
      })
      .then((mediaId) => {
        document.dispatchEvent(new CustomEvent('MediaRemoved', {
          detail: { mediaId: mediaId }
        }))
      })
      .catch((response) => {
        this.showValidationModal((isUndo ? 'Undo' : 'Remove') + ' Media Error', 'Failed to ' + (isUndo ? 'undo' : 'remove') + ' media. Please try again later')
      })
  }

  showValidationModal (title, message) {
    this.validationModalTitleTarget.innerHTML = title
    this.validationModalMessageTarget.innerHTML = message
    this.validationModal.show()
  }
}
